/**
 * Teams Hub Dashboard
 * 
 * Activity feed, mentions, and team presence overview.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  Activity, 
  AtSign, 
  Users, 
  Hash, 
  MessageSquare, 
  StickyNote,
  Bell,
  Sparkles,
  ChevronRight,
  Circle,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import { cn, formatRelativeTime, getInitials, getAvatarColor, getPresenceConfig } from '../lib/utils'
import { activityApi, channelsApi, usersApi } from '../lib/api'
import { useTeamsStore } from '../store/teamsStore'

export default function DashboardPage() {
  const navigate = useNavigate()
  const { setChannels, setUsers, setUnreadCounts } = useTeamsStore()
  const [mentionsOpen, setMentionsOpen] = useState(false)
  
  // Fetch activity
  const { data: activities = [], isLoading: loadingActivity, refetch: refetchActivity } = useQuery({
    queryKey: ['teams-activity'],
    queryFn: () => activityApi.list(30),
    refetchInterval: 30000, // Refresh every 30s
  })
  
  // Fetch mentions
  const { data: mentions = [], isLoading: loadingMentions } = useQuery({
    queryKey: ['teams-mentions'],
    queryFn: activityApi.getMentions,
    refetchInterval: 60000,
  })
  
  // Fetch channels for sidebar
  const { data: channels = [] } = useQuery({
    queryKey: ['teams-channels'],
    queryFn: channelsApi.list,
    onSuccess: (data) => {
      setChannels(data)
      const unreadChannels = data.filter(c => c.type === 'group' && c.unread_count > 0).length
      const unreadDMs = data.filter(c => c.type === 'direct' && c.unread_count > 0).length
      setUnreadCounts(unreadChannels, unreadDMs)
    },
  })
  
  // Fetch team members
  const { data: users = [] } = useQuery({
    queryKey: ['teams-users'],
    queryFn: usersApi.list,
    onSuccess: setUsers,
  })
  
  const onlineUsers = users.filter(u => u.is_online && !u.is_self)
  
  return (
    <div className="h-full bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Welcome to Teams
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Stay connected with your team through channels, messages, and notes.
          </p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Team Online"
            value={onlineUsers.length}
            subLabel={`of ${users.filter(u => !u.is_self).length} members`}
            color="emerald"
            onClick={() => navigate('/people')}
          />
          <StatCard
            icon={Hash}
            label="Active Channels"
            value={channels.filter(c => c.type !== 'direct').length}
            subLabel="group discussions"
            color="violet"
            onClick={() => navigate('/channels')}
          />
          <StatCard
            icon={AtSign}
            label="Mentions"
            value={mentions.length}
            subLabel="unread mentions"
            color="amber"
            onClick={() => setMentionsOpen((open) => !open)}
          />
          <StatCard
            icon={MessageSquare}
            label="Unread Messages"
            value={channels.reduce((sum, c) => sum + c.unread_count, 0)}
            subLabel="across all channels"
            color="blue"
            onClick={() => navigate('/messages')}
          />
        </div>

        {mentionsOpen && (
          <section aria-label="Mentions" className="mb-8 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
              <h2 className="font-semibold text-slate-800 dark:text-white">Mentions</h2>
            </div>
            {loadingMentions ? (
              <p className="px-5 py-6 text-sm text-slate-400">Loading mentions...</p>
            ) : mentions.length === 0 ? (
              <p className="px-5 py-6 text-sm text-slate-500">No mentions.</p>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {mentions.map((mention) => (
                  <ActivityItem key={mention.id} activity={mention} />
                ))}
              </div>
            )}
          </section>
        )}
        
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity Feed */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-violet-600" />
                  <h2 className="font-semibold text-slate-800 dark:text-white">Recent Activity</h2>
                </div>
                <button
                  onClick={() => refetchActivity()}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <RefreshCw className={cn("w-4 h-4 text-slate-400", loadingActivity && "animate-spin")} />
                </button>
              </div>
              
              <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-[500px] overflow-y-auto custom-scrollbar">
                {loadingActivity ? (
                  <div className="py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-violet-500 mb-2" />
                    <p className="text-sm text-slate-400">Loading activity...</p>
                  </div>
                ) : activities.length === 0 ? (
                  <div className="py-12 text-center">
                    <Sparkles className="w-8 h-8 mx-auto text-slate-300 mb-3" />
                    <p className="text-sm text-slate-500 mb-1">No activity yet</p>
                    <p className="text-xs text-slate-400">Start a conversation to see activity here</p>
                  </div>
                ) : (
                  activities.map((activity) => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))
                )}
              </div>
            </div>
          </div>
          
          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Online Team */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  <h2 className="font-semibold text-slate-800 dark:text-white">Online Now</h2>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                    {onlineUsers.length}
                  </span>
                </div>
              </div>
              
              <div className="p-3 space-y-1 max-h-[250px] overflow-y-auto custom-scrollbar">
                {onlineUsers.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No one else is online</p>
                ) : (
                  onlineUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => navigate(`/messages?user=${user.id}`)}
                      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                    >
                      <div className="relative">
                        <div className={cn(
                          "w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-gradient-to-br",
                          getAvatarColor(user.name)
                        )}>
                          {getInitials(user.name, user.email)}
                        </div>
                        <div className={cn(
                          "absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white dark:ring-slate-800",
                          getPresenceConfig(user.status).color
                        )} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 dark:text-white truncate">{user.name}</p>
                        <p className="text-xs text-slate-400 truncate">{user.custom_status || user.status}</p>
                      </div>
                      <MessageSquare className="w-4 h-4 text-slate-400" />
                    </button>
                  ))
                )}
              </div>
              
              <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-700">
                <button
                  onClick={() => navigate('/people')}
                  className="w-full text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center justify-center gap-1"
                >
                  View all people
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Recent Channels */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <Hash className="w-5 h-5 text-violet-600" />
                  <h2 className="font-semibold text-slate-800 dark:text-white">Channels</h2>
                </div>
              </div>
              
              <div className="p-3 space-y-1 max-h-[200px] overflow-y-auto custom-scrollbar">
                {channels.filter(c => c.type !== 'direct').slice(0, 5).map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() => navigate(`/channels/${channel.id}`)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                      <Hash className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-white truncate">{channel.name}</p>
                      <p className="text-xs text-slate-400 truncate">{channel.member_count} members</p>
                    </div>
                    {channel.unread_count > 0 && (
                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-violet-500 text-white">
                        {channel.unread_count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              
              <div className="px-3 py-2 border-t border-slate-100 dark:border-slate-700">
                <button
                  onClick={() => navigate('/channels')}
                  className="w-full text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center justify-center gap-1"
                >
                  View all channels
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Stat Card Component
function StatCard({ 
  icon: Icon, 
  label, 
  value, 
  subLabel, 
  color, 
  onClick 
}: {
  icon: React.ElementType
  label: string
  value: number
  subLabel: string
  color: 'emerald' | 'violet' | 'amber' | 'blue'
  onClick: () => void
}) {
  const colorClasses = {
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
    violet: 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  }
  
  return (
    <button
      onClick={onClick}
      className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 text-left hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600 transition-all"
    >
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3", colorClasses[color])}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-slate-900 dark:text-white mb-1">{value}</p>
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</p>
      <p className="text-xs text-slate-400 mt-0.5">{subLabel}</p>
    </button>
  )
}

// Activity Item Component
function ActivityItem({ activity }: { activity: any }) {
  const iconMap: Record<string, React.ElementType> = {
    message: MessageSquare,
    mention: AtSign,
    note: StickyNote,
    announcement: Bell,
    channel_created: Hash,
  }
  
  const Icon = iconMap[activity.type] || Activity
  
  return (
    <div className="flex items-start gap-4 px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
      <div className={cn(
        "w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-gradient-to-br",
        getAvatarColor(activity.actor?.name)
      )}>
        {getInitials(activity.actor?.name || 'Unknown', '')}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 dark:text-slate-200">
          <span className="font-semibold">{activity.actor?.name || 'Someone'}</span>
          {' '}
          {activity.type === 'message' && 'sent a message in'}
          {activity.type === 'mention' && 'mentioned you in'}
          {activity.type === 'note' && 'added a note to'}
          {activity.type === 'channel_created' && 'created channel'}
          {' '}
          {activity.target && (
            <span className="font-medium text-violet-600 dark:text-violet-400">
              {activity.target.type === 'channel' ? `#${activity.target.name}` : activity.target.name}
            </span>
          )}
        </p>
        {activity.content && (
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 truncate">
            "{activity.content}"
          </p>
        )}
        <p className="text-xs text-slate-400 mt-1">
          {formatRelativeTime(activity.created_at)}
        </p>
      </div>
      
      <Icon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
    </div>
  )
}
